import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import { api } from "../api";
import * as mock from "../mock";

// Build default content from mock so the site never renders empty.
const DEFAULT_CONTENT = {
  company: mock.company,
  hero: {
    titleLead: "Global Pharmaceutical",
    titleHighlight: "global healthcare",
    titleTail: "Made Simple",
    image: mock.images.heroLab,
    slides: mock.heroSlides,
    interval: mock.heroInterval,
  },
  images: mock.images,
  stats: mock.stats,
  whyChoose: mock.whyChoose,
  services: mock.services,
  productCategories: mock.productCategories,
  dosageForms: mock.dosageForms,
  regions: mock.regions,
  certifications: mock.certifications,
  processSteps: mock.processSteps,
  testimonials: mock.testimonials,
  site_status: { mode: "live", title: "", message: "", launch_date: "" },
  featuredSlugs: mock.featuredProducts.map((p) => p.slug),
};

const ContentContext = createContext(null);

export function ContentProvider({ children }) {
  const [content, setContent] = useState(DEFAULT_CONTENT);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const [c, p] = await Promise.all([
        api.get("/content"),
        api.get("/products"),
      ]);
      if (c.data && Object.keys(c.data).length) {
        setContent({
          ...DEFAULT_CONTENT,
          ...c.data,
          // merge hero so newly added fields (slides, interval) fall back to defaults
          hero: { ...DEFAULT_CONTENT.hero, ...(c.data.hero || {}) },
        });
      }
      if (Array.isArray(p.data)) setProducts(p.data);
    } catch (e) {
      // Fall back to defaults on failure
      console.error("Content load failed", e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const featured = useMemo(() => {
    const slugs = content.featuredSlugs || [];
    const bySlug = Object.fromEntries(products.map((p) => [p.slug, p]));
    const picked = slugs.map((s) => bySlug[s]).filter(Boolean);
    return picked.length ? picked : products.slice(0, 6);
  }, [content, products]);

  const value = useMemo(
    () => ({ content, products, featured, loading, refresh: load }),
    [content, products, featured, loading, load]
  );

  return <ContentContext.Provider value={value}>{children}</ContentContext.Provider>;
}

export function useContent() {
  const ctx = useContext(ContentContext);
  if (!ctx) throw new Error("useContent must be used within ContentProvider");
  return ctx;
}
